import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useAccessToken, useConfig, useCustomerId} from '@salesforce/commerce-sdk-react'
import {helpers} from 'commerce-sdk-isomorphic'

interface UpdateProfilePayload {
    trigger: 'register' | 'login'
    smsOptIn: boolean
    loyaltyOptIn: boolean
    tandc: boolean
    deadline: string | string[]
    phone: string
}

const useUpdateProfile = () => {
    const {getTokenWhenReady} = useAccessToken()
    const config = useConfig()
    const queryClient = useQueryClient()
    const customerId = useCustomerId()

    return useMutation(
        async (payload: UpdateProfilePayload) => {
            const accessToken = await getTokenWhenReady()

            const response = await helpers.callCustomEndpoint({
                options: {
                    method: 'PATCH',
                    customApiPathParameters: {
                        apiName: 'profile',
                        endpointPath: 'updateprofile'
                    },
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                },
                clientConfig: {
                    parameters: {
                        clientId: config.clientId,
                        siteId: config.siteId,
                        organizationId: config.organizationId,
                        shortCode: config.shortCode
                    },
                    proxy: config.proxy,
                    throwOnBadResponse: true
                },
                rawResponse: false
            })

            return response
        },
        {
            onSuccess: (_data, variables) => {
                if (variables.loyaltyOptIn) {
                    // Refetch customer so c_acLoyaltyData is picked up, which
                    // gates the checkEnrollment query in usePerksAndRewards.
                    void queryClient.invalidateQueries({
                        queryKey: [
                            '/commerce-sdk-react',
                            '/organizations/',
                            config.organizationId,
                            '/customers/',
                            customerId
                        ]
                    })
                    void queryClient.invalidateQueries({
                        queryKey: ['checkEnrollment']
                    })
                }
            },
            onError: (error: unknown) => {
                console.error(`[useUpdateProfile] Failed to update profile:`, error)
            }
        }
    )
}

export default useUpdateProfile
